import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    this.syncPriceRangeVisibility();
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);

    // Update volume pricing note
    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }

    // variant:update puede dispararse antes de que el `?variant=` esté actualizado en la URL.
    // Sincronizamos una vez ahora y otra vez en el siguiente frame para respetar
    // estrictamente: "rango solo cuando NO hay ?variant=".
    this.syncPriceRangeVisibility();
    requestAnimationFrame(() => this.syncPriceRangeVisibility());
  };

  /**
   * Toggles:
   * - Min-Max range container visible when URL has NO `?variant=`
   * - Selected-price container visible when URL has `?variant=`
   */
  /**
   * Toggles based on the actual URL state.
   * Range visible only when URL has NO `?variant=`.
   */
  syncPriceRangeVisibility = () => {
    const rangeEl = this.querySelector('[data-price-range-container]');
    if (!rangeEl) return;

    const selectedEl = this.querySelector('[data-price-selected-container]');
    if (!selectedEl) return;

    const urlHasVariant = new URL(window.location.href).searchParams.has('variant');
    const showSelected = urlHasVariant;

    const range = /** @type {HTMLElement} */ (rangeEl);
    const selected = /** @type {HTMLElement} */ (selectedEl);

    // The markup uses the `hidden` attribute on the selected container.
    // Toggle attributes to guarantee the browser actually shows/hides elements.
    if (showSelected) {
      range.setAttribute('hidden', '');
      selected.removeAttribute('hidden');
    } else {
      selected.setAttribute('hidden', '');
      range.removeAttribute('hidden');
    }

    // Hide/show volume pricing note to match the visible mode.
    const noteEl = this.querySelector('.volume-pricing-note');
    if (noteEl instanceof HTMLElement) noteEl.style.display = !showSelected ? 'none' : '';
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
